import { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { useDispatch } from 'react-redux'
import './myaccount.css';
import apiProvider from '../../apiProvider/api';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { login } from '../../redux/authSlice'
import { toast } from 'react-toastify';

import { Helmet } from 'react-helmet-async';
import { useTranslation } from "../../context/TranslationContext";



const SigninSchema = Yup.object().shape({
    email: Yup.string().email('Please enter a valid email address').required('Email address is required'),
    password: Yup.string().required('Please enter your password')
});

const sendPassword = Yup.object().shape({
    email: Yup.string().email('Please enter a valid email address').required('Email address is required'),
});

const otpSchema = Yup.object().shape({
    otp: Yup.string()
        .matches(/^[0-9]{4}$/, 'OTP must be exactly 4 digits')
        .required('OTP is required')
});

const resetPasswordSchema = Yup.object().shape({
    password: Yup.string()
        .min(6, 'Password must be at least 6 characters')
        .required('Password is required'),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
        .required('Confirm password is required')
});

const LoginPage = () => {

    const navigate = useNavigate();
    const disPatch = useDispatch()
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [forgotPasswordStep, setForgotPasswordStep] = useState('email'); // 'email' | 'otp' | 'reset'
    const [userEmail, setUserEmail] = useState('');
    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [showResetPassword, setShowResetPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const { translateSync, currentLanguage, setCurrentLanguage } = useTranslation();


    const formSubmit = async (data) => {
        try {
            const guestUserId = localStorage.getItem('guestUserId');
            const input = {
                email: data.email,
                password: data.password,
                guestUserId: guestUserId || undefined
            };
            const result = await apiProvider.login(input);
            console.log(result, "resss");

            // Check for success (apiProvider might return status: true/false or HTTP status)
            if (result && (result.status === 200 || result.status === true) && result.response) {
                // Handle response structure: axios response -> data -> data -> token
                const responseData = result.response.data || result.response;
                const token = responseData.data?.token || responseData.token;

                if (token) {
                    localStorage.setItem('userToken', token);
                    localStorage.setItem('user', JSON.stringify(responseData.data?.user || responseData.user || {}));
                    disPatch(login({ token: token }));

                    // Clear guest user ID as it is now merged
                    localStorage.removeItem('guestUserId');
                    localStorage.removeItem('guestCart'); // Clear local guest cart copy if any

                    setTimeout(() => {
                        navigate("/"); // Go to home page
                    }, 1500);

                    return;
                }
            }

            // Extract error message robustly from all possible response shapes
            let measge = "";
            if (result) {
                if (result.response?.response?.data?.message) {
                    measge = result.response.response.data.message;
                } else if (result.response?.data?.message) {
                    measge = result.response.data.message;
                } else if (result.response?.message) {
                    measge = result.response.message;
                } else if (result.message) {
                    measge = result.message;
                }
            }

            // Check for error message and show toast
            if (measge) {
                console.log("Login error message extracted:", measge);

                // Theme specific error messages
                if (measge.toLowerCase().includes("email incorrect")) {
                    measge = "Email incorrect";
                } else if (measge.toLowerCase().includes("password incorrect")) {
                    measge = "Password incorrect";
                } else if (measge.toLowerCase().includes("user not found") || measge.toLowerCase().includes("invalid")) {
                    measge = "We couldn't find an account with those details. Please check or Sign Up.";
                } else if (measge.toLowerCase().includes("password")) {
                    measge = "The password you entered is incorrect. Please try again.";
                }

                toast.error(measge, {
                    autoClose: 3000,
                    style: { fontFamily: "'Lato', sans-serif" }
                });

            } else {
                console.log("No error message extracted from result:", result);
                toast.error("Login failed. Please try again.", { style: { fontFamily: "'Lato', sans-serif" } });
            }
        } catch (err) {
            console.error("Login page onSubmit error:", err);
            toast.error("An error occurred. Please try again later.", { style: { fontFamily: "'Lato', sans-serif" } });
        }
    };

    const handleRequestOtp = async (data) => {
        try {
            const input = { email: data.email };
            const result = await apiProvider.requestForgetPasswordOtp(input);

            if (result && result.status === 200) {
                setUserEmail(data.email);
                setForgotPasswordStep('reset');
                toast.success("User verified successfully!", { autoClose: 2000 });
            } else {
                const errorMsg = result?.response?.response?.data?.message || "Failed to verify email";
                toast.error(errorMsg, { autoClose: 3000 });
            }
        } catch (err) {
            toast.error("An error occurred. Please try again later.");
        }
    };

    const handleVerifyOtp = async (data) => {
        try {
            const input = { email: userEmail, otp: data.otp };
            const result = await apiProvider.verifyForgetPasswordOtp(input);

            if (result && result.status === 200) {
                setForgotPasswordStep('reset');
                toast.success("OTP verified successfully!", { autoClose: 1500 });
            } else {
                const errorMsg = result?.response?.response?.data?.message || "Invalid OTP";
                toast.error(errorMsg, { autoClose: 2000 });
            }
        } catch (err) {
            toast.error("An error occurred. Please try again later.");
        }
    };

    const handleResetPassword = async (data) => {
        try {
            const input = { email: userEmail, password: data.password, confirmPassword: data.confirmPassword };
            const result = await apiProvider.resetPassword(input);

            if (result && result.status === 200) {
                toast.success("Password updated successfully!", { autoClose: 1500 });
                setTimeout(() => {
                    setShowForgotPassword(false);
                    setForgotPasswordStep('email');
                    setUserEmail('');
                }, 1500);
            } else {
                const errorMsg = result?.response?.response?.data?.message || "Failed to reset password";
                toast.error(errorMsg, { autoClose: 2000 });
            }
        } catch (err) {
            toast.error("An error occurred. Please try again later.");
        }
    };

    const handleBackToLogin = () => {
        setShowForgotPassword(false);
        setForgotPasswordStep('email');
        setUserEmail('');
    };

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])
    return (
        <>

            <Helmet>
                <title>Login | Colours Boutique</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>
            <div className="auth-page-wrapper" style={{
                backgroundColor: "#ebe7e1",
                backgroundImage: "url('https://www.transparenttextures.com/patterns/natural-paper.png')",
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                paddingTop: "160px",
                paddingBottom: "50px",
                fontFamily: "'Lato', sans-serif"
            }}>
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-5 col-md-8">
                            <div className="bg-white p-5 shadow-sm border border-light" style={{ position: "relative" }}>
                                {/* Decorative Border Top */}
                                <div style={{
                                    position: "absolute",
                                    top: "0",
                                    left: "0",
                                    right: "0",
                                    height: "4px",
                                    background: "linear-gradient(to right, #d4af37, #800000)"
                                }}></div>

                                <div className="text-center mb-5">
                                    <h2 className="fw-bold text-uppercase" style={{ fontFamily: "'Playfair Display', serif", letterSpacing: "2px", color: "#3a3530" }}>
                                        {showForgotPassword ? translateSync('Reset Password') : translateSync('Welcome Back')}
                                    </h2>
                                    <p className="text-muted small text-uppercase" style={{ letterSpacing: "1px" }}>
                                        {showForgotPassword ? translateSync('Enter your email to recover your account') : translateSync('Sign in to your account')}
                                    </p>
                                </div>

                                {showForgotPassword ?
                                    <>
                                        {forgotPasswordStep === 'email' && (
                                            <Formik
                                                initialValues={{ email: '' }}
                                                validationSchema={sendPassword}
                                                onSubmit={handleRequestOtp}
                                            >
                                                {({ errors, touched, handleChange, handleBlur }) => (
                                                    <Form noValidate>
                                                        <div className="mb-3">
                                                            <label className="bs-form-label small text-uppercase fw-bold mb-1 text-muted" style={{ fontSize: "0.75rem" }}>{translateSync('Email Address')}</label>
                                                            <input
                                                                className='form-control rounded-0 py-2 px-3'
                                                                name="email"
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                placeholder={translateSync('Enter your email')}
                                                                type="email"
                                                                style={{ border: "1px solid #ddd", fontSize: "0.9rem" }}
                                                            />
                                                            {errors.email && touched.email && <div className="text-danger small mt-1 text-start">{errors.email}</div>}
                                                        </div>

                                                        <div className="mt-4">
                                                            <button type="submit" className="btn btn-dark w-100 rounded-0 py-2 text-uppercase fw-bold" style={{ letterSpacing: "1px" }}>
                                                                {translateSync('Verify Email')}
                                                            </button>
                                                        </div>

                                                        <div className="text-center mt-4">
                                                            <a
                                                                className="text-muted small text-decoration-underline text-uppercase cursor-pointer"
                                                                style={{ cursor: "pointer", fontSize: "0.8rem" }}
                                                                onClick={handleBackToLogin}
                                                            >
                                                                {translateSync('Back to Login')}
                                                            </a>
                                                        </div>
                                                    </Form>
                                                )}
                                            </Formik>
                                        )}

                                        {forgotPasswordStep === 'otp' && (
                                            <Formik
                                                initialValues={{ otp: '' }}
                                                validationSchema={otpSchema}
                                                onSubmit={handleVerifyOtp}
                                            >
                                                {({ errors, touched, handleChange, handleBlur }) => (
                                                    <Form noValidate>
                                                        <div className="text-center mb-4">
                                                            <p className="text-muted small mb-0">Enter the 4-digit OTP sent to</p>
                                                            <p className="fw-bold small">{userEmail}</p>
                                                        </div>
                                                        <div className="mb-3">
                                                            <label className="bs-form-label small text-uppercase fw-bold mb-1 text-muted d-block text-center" style={{ fontSize: "0.75rem" }}>{translateSync('Enter OTP')}</label>
                                                            <input
                                                                className='form-control rounded-0 py-2 px-3 text-center fw-bold'
                                                                name="otp"
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                placeholder="0000"
                                                                type="text"
                                                                maxLength="4"
                                                                style={{ border: "1px solid #ddd", fontSize: "1.2rem", letterSpacing: "0.5rem" }}
                                                            />
                                                            {errors.otp && touched.otp && <div className="text-danger small mt-1 text-start">{errors.otp}</div>}
                                                        </div>

                                                        <div className="mt-4">
                                                            <button type="submit" className="btn btn-dark w-100 rounded-0 py-2 text-uppercase fw-bold" style={{ letterSpacing: "1px" }}>
                                                                {translateSync('Verify OTP')}
                                                            </button>
                                                        </div>

                                                        <div className="text-center mt-4">
                                                            <a
                                                                className="text-muted small text-decoration-underline text-uppercase cursor-pointer d-block mb-2"
                                                                style={{ cursor: "pointer", fontSize: "0.8rem" }}
                                                                onClick={() => setForgotPasswordStep('email')}
                                                            >
                                                                {translateSync('Change Email')}
                                                            </a>
                                                            <a
                                                                className="text-muted small text-decoration-underline text-uppercase cursor-pointer"
                                                                style={{ cursor: "pointer", fontSize: "0.8rem" }}
                                                                onClick={handleBackToLogin}
                                                            >
                                                                {translateSync('Back to Login')}
                                                            </a>
                                                        </div>
                                                    </Form>
                                                )}
                                            </Formik>
                                        )}

                                        {forgotPasswordStep === 'reset' && (
                                            <Formik
                                                initialValues={{ password: '', confirmPassword: '' }}
                                                validationSchema={resetPasswordSchema}
                                                onSubmit={handleResetPassword}
                                            >
                                                {({ errors, touched, handleChange, handleBlur }) => (
                                                    <Form noValidate>
                                                        <div className="mb-3">
                                                            <label className="bs-form-label small text-uppercase fw-bold mb-1 text-muted" style={{ fontSize: "0.75rem" }}>{translateSync('New Password')}</label>
                                                            <div className="password-input-wrapper">
                                                                <input
                                                                    className='form-control rounded-0 py-2 px-3'
                                                                    name="password"
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    placeholder={translateSync('Enter new password')}
                                                                    type={showResetPassword ? "text" : "password"}
                                                                    style={{ border: "1px solid #ddd", fontSize: "0.9rem" }}
                                                                />
                                                                <span
                                                                    className="password-toggle-icon"
                                                                    onClick={() => setShowResetPassword(!showResetPassword)}
                                                                >
                                                                    <i className={`bi ${showResetPassword ? 'bi-eye' : 'bi-eye-slash'}`}></i>
                                                                </span>
                                                            </div>
                                                            {errors.password && touched.password && <div className="text-danger small mt-1 text-start">{errors.password}</div>}
                                                        </div>

                                                        <div className="mb-3">
                                                            <label className="bs-form-label small text-uppercase fw-bold mb-1 text-muted" style={{ fontSize: "0.75rem" }}>{translateSync('Confirm Password')}</label>
                                                            <div className="password-input-wrapper">
                                                                <input
                                                                    className='form-control rounded-0 py-2 px-3'
                                                                    name="confirmPassword"
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    placeholder={translateSync('Confirm new password')}
                                                                    type={showConfirmPassword ? "text" : "password"}
                                                                    style={{ border: "1px solid #ddd", fontSize: "0.9rem" }}
                                                                />
                                                                <span
                                                                    className="password-toggle-icon"
                                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                                >
                                                                    <i className={`bi ${showConfirmPassword ? 'bi-eye' : 'bi-eye-slash'}`}></i>
                                                                </span>
                                                            </div>
                                                            {errors.confirmPassword && touched.confirmPassword && <div className="text-danger small mt-1 text-start">{errors.confirmPassword}</div>}
                                                        </div>

                                                        <div className="mt-4">
                                                            <button type="submit" className="btn btn-dark w-100 rounded-0 py-2 text-uppercase fw-bold" style={{ letterSpacing: "1px" }}>
                                                                {translateSync('Reset Password')}
                                                            </button>
                                                        </div>

                                                        <div className="text-center mt-4">
                                                            <a
                                                                className="text-muted small text-decoration-underline text-uppercase cursor-pointer"
                                                                style={{ cursor: "pointer", fontSize: "0.8rem" }}
                                                                onClick={handleBackToLogin}
                                                            >
                                                                {translateSync('Back to Login')}
                                                            </a>
                                                        </div>
                                                    </Form>
                                                )}
                                            </Formik>
                                        )}
                                    </>
                                    :
                                    <>
                                        <Formik
                                            initialValues={{
                                                email: '',
                                                password: '',
                                            }}
                                            validationSchema={SigninSchema}
                                            onSubmit={values => {
                                                formSubmit(values)
                                            }}
                                        >
                                            {({ errors, touched, handleChange, handleBlur }) => (
                                                <Form noValidate>
                                                    <div className="mb-3">
                                                        <label className="bs-form-label small text-uppercase fw-bold mb-1 text-muted" style={{ fontSize: "0.75rem" }}>{translateSync('Email Address')}</label>
                                                        <input
                                                            className='form-control rounded-0 py-2 px-3'
                                                            name="email"
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            placeholder={translateSync('Enter your email')}
                                                            type="email"
                                                            style={{ border: "1px solid #ddd", fontSize: "0.9rem" }}
                                                        />
                                                        {errors.email && touched.email && <div className="text-danger small mt-1 text-start">{errors.email}</div>}
                                                    </div>

                                                    <div className="mb-3">
                                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                                            <label className="bs-form-label small text-uppercase fw-bold text-muted" style={{ fontSize: "0.75rem" }}>{translateSync('Password')}</label>
                                                            <a
                                                                className="text-muted small text-decoration-underline"
                                                                style={{ fontSize: "0.75rem", cursor: "pointer", whiteSpace: "nowrap" }}
                                                                onClick={() => setShowForgotPassword(true)}
                                                            >
                                                                {translateSync('Forgot Password?')}
                                                            </a>
                                                        </div>
                                                        <div className="password-input-wrapper">
                                                            <input
                                                                className='form-control rounded-0 py-2 px-3'
                                                                name="password"
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                placeholder={translateSync('Enter your password')}
                                                                type={showLoginPassword ? "text" : "password"}
                                                                style={{ border: "1px solid #ddd", fontSize: "0.9rem" }}
                                                            />
                                                            <span
                                                                className="password-toggle-icon"
                                                                onClick={() => setShowLoginPassword(!showLoginPassword)}
                                                            >
                                                                <i className={`bi ${showLoginPassword ? 'bi-eye' : 'bi-eye-slash'}`}></i>
                                                            </span>
                                                        </div>
                                                        {errors.password && touched.password && <div className="text-danger small mt-1 text-start">{errors.password}</div>}
                                                    </div>

                                                    <div className="mt-4">
                                                        <button type="submit" className="btn btn-dark w-100 rounded-0 py-2 text-uppercase fw-bold" style={{ letterSpacing: "1px" }}>
                                                            {translateSync('Log In')}
                                                        </button>
                                                    </div>

                                                    <div className="text-center mt-4 pt-3 border-top">
                                                        <p className="small text-muted mb-0">
                                                            {translateSync("Don't have an account?")}
                                                            <span
                                                                className="fw-bold ms-1 text-dark text-uppercase cursor-pointer"
                                                                style={{ cursor: "pointer", textDecoration: "underline" }}
                                                                onClick={() => navigate("/register")}
                                                            >
                                                                {translateSync('Register Now')}
                                                            </span>
                                                        </p>
                                                    </div>

                                                </Form>
                                            )}
                                        </Formik>
                                    </>
                                }
                            </div>
                        </div>
                    </div>
                </div>


            </div>

        </>
    )
}

export default LoginPage;